import type { ComplianceInformationInterface } from '@interfaces/ComplianceInformation.interface';

export type MappedComplianceYoyTone = 'positive' | 'negative' | 'neutral';

export type MappedComplianceCards = {
  financials: {
    tags: { label: string; tone?: 'error' | 'neutral' | 'brand' }[];
    metrics: {
      label: string;
      value: string;
      yoy?: string;
      yoyTone?: MappedComplianceYoyTone;
    }[];
    yearColumns: string[];
    tableCaption: string;
    defaultExpandedSectionIds: string[];
    sections: {
      id: string;
      title: string;
      lineCount: number;
      viewAllLabel?: string;
      rows?: { label: string; values: string[] }[];
    }[];
    alert?: string;
  };
  groupStructure: {
    sourceTag: string;
    groupCount: number;
    nodes: {
      id: string;
      name: string;
      country?: string;
      ownership?: string;
      isGuo?: boolean;
      emphasized?: boolean;
      children?: MappedComplianceCards['groupStructure']['nodes'];
    }[];
    footerLabel: string;
    sourceLinkLabel: string;
  };
  shareholders: {
    tags: { label: string; tone?: 'neutral' | 'brand' }[];
    coveragePercent: string;
    coverageCaption: string;
    coverageValue: number;
    statusCaption?: string;
    holders: {
      id: string;
      country?: string;
      name: string;
      subtitle?: string;
      holding: string;
      holdingCaption?: string;
      details?: { label: string; value: string }[];
    }[];
    defaultExpandedHolderIds: string[];
    ubo?: { label: string; value: string };
    footerLabel: string;
    footerLinkLabel: string;
  };
  creditRisk: {
    sourceTag: string;
    metrics: {
      label: string;
      value: string;
      suffix?: string;
      caption?: string;
    }[];
    signals: {
      id: string;
      title: string;
      source: string;
      value: string;
      caption?: string;
      captionTone?: MappedComplianceYoyTone;
    }[];
  };
};

export function hasOrbisComplianceData(
  data: ComplianceInformationInterface | undefined
): boolean {
  const company = data?.orbis?.company_data;
  const owners = data?.orbis?.beneficial_owners ?? [];
  return Boolean(
    company?.name ||
      company?.orbis_id ||
      company?.bvd_id_number ||
      owners.length
  );
}

export function formatCompactGbp(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    return `${sign}£${trimFixed(abs / 1_000_000)}m`;
  }
  if (abs >= 1_000) {
    return `${sign}£${trimFixed(abs / 1_000)}k`;
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
  }).format(n);
}

export function formatHolding(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(2);
}

export function yoyFromValues(
  current?: number,
  previous?: number
): { yoy?: string; yoyTone?: MappedComplianceYoyTone } {
  if (
    current == null ||
    previous == null ||
    Number.isNaN(current) ||
    Number.isNaN(previous) ||
    previous === 0
  ) {
    return {};
  }
  const delta = ((current - previous) / Math.abs(previous)) * 100;
  const tone: MappedComplianceYoyTone =
    delta > 0.05 ? 'positive' : delta < -0.05 ? 'negative' : 'neutral';
  const prefix = delta > 0 ? '+' : '';
  return {
    yoy: `${prefix}${delta.toFixed(1)}% YoY`,
    yoyTone: tone
  };
}

function trimFixed(value: number): string {
  return value.toFixed(1).replace(/\.0$/, '');
}

function countryCode(value?: string | null): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
  return undefined;
}

function flagged(value: boolean | string | null | undefined): boolean {
  return value === true || value === 'true' || value === 'Yes';
}

export function mapOrbisToComplianceCards(
  data?: ComplianceInformationInterface | null
): MappedComplianceCards {
  const company = data?.orbis?.company_data;
  const owners = data?.orbis?.beneficial_owners ?? [];
  const turnover = company?.annual_turnover ?? [];
  const latestTurnover = Number(turnover[0] ?? 0);
  const previousTurnover = Number(turnover[1] ?? 0);
  const turnoverYoy = yoyFromValues(latestTurnover, previousTurnover);
  const accountsOverdue = Boolean(data?.companies_house?.accounts?.overdue);
  const nextDue = data?.companies_house?.accounts?.next_due;

  const tags: MappedComplianceCards['financials']['tags'] = [
    { label: 'Orbis' }
  ];
  if (accountsOverdue) {
    tags.unshift({ label: 'Accounts overdue', tone: 'error' });
  }

  const yearColumns = turnover.map((_, index) =>
    index === 0 ? 'Latest' : `-${index}`
  );

  const coverage = Math.min(
    100,
    owners.reduce((sum, owner) => sum + Number(owner.total_pct || owner.direct_pct || 0), 0)
  );
  const ubo = owners.find((owner) => owner.ubo);
  const companyOwners = owners.filter((owner) => owner.type === 'company');
  const companyName = company?.name || 'This company';
  const companyCountry = countryCode(company?.address?.country);

  return {
    financials: {
      tags,
      metrics: [
        {
          label: 'Turnover',
          value: formatCompactGbp(latestTurnover || undefined),
          ...turnoverYoy
        },
        {
          label: 'Profit before tax',
          value: '—'
        },
        {
          label: 'Total assets',
          value: formatCompactGbp(company?.total_assets || undefined)
        },
        {
          label: 'Employees',
          value:
            company?.num_of_employees != null
              ? String(company.num_of_employees)
              : '—'
        }
      ],
      yearColumns,
      tableCaption: 'GBP, as filed',
      defaultExpandedSectionIds: ['profit-and-loss'],
      sections: [
        {
          id: 'profit-and-loss',
          title: 'Profit & loss',
          lineCount: 1,
          viewAllLabel: 'View in Orbis',
          rows: [
            {
              label: 'Operating revenue (turnover)',
              values: turnover.map((value) =>
                Number(value)
                  ? Number(value).toLocaleString('en-GB')
                  : '—'
              )
            }
          ]
        },
        {
          id: 'balance-sheet',
          title: 'Balance sheet',
          lineCount: 1,
          rows: [
            {
              label: 'Total assets',
              values: [
                company?.total_assets
                  ? Number(company.total_assets).toLocaleString('en-GB')
                  : '—'
              ]
            }
          ]
        }
      ],
      alert: accountsOverdue
        ? nextDue
          ? `Accounts overdue since ${nextDue}`
          : 'Accounts overdue'
        : undefined
    },
    groupStructure: {
      sourceTag: 'Orbis',
      groupCount: 1 + companyOwners.length,
      nodes: [
        {
          id: company?.bvd_id_number || company?.orbis_id || 'subject',
          name: companyName,
          country: companyCountry,
          isGuo: true,
          emphasized: true,
          children: companyOwners.map((owner, index) => ({
            id: owner.uci || `owner-${index}`,
            name: owner.name,
            country: countryCode(owner.country_iso),
            ownership: formatHolding(owner.total_pct ?? owner.direct_pct)
          }))
        }
      ],
      footerLabel: `Showing ${1 + companyOwners.length} of ${
        1 + companyOwners.length
      } entities`,
      sourceLinkLabel: 'Orbis'
    },
    shareholders: {
      tags: [
        {
          label: `${owners.length} shareholder${owners.length === 1 ? '' : 's'}`
        },
        { label: 'Orbis' }
      ],
      coveragePercent: `${Math.round(coverage)}%`,
      coverageCaption: 'of identified share capital',
      coverageValue: coverage,
      statusCaption: ubo
        ? `${ubo.name} · ultimate beneficial owner`
        : undefined,
      holders: owners.map((owner, index) => ({
        id: owner.uci || `holder-${index}`,
        country: countryCode(owner.country_iso),
        name: owner.name,
        subtitle: owner.type === 'company' ? 'Corporate shareholder' : 'Individual',
        holding: `${formatHolding(owner.total_pct ?? owner.direct_pct)}%`,
        holdingCaption: owner.direct_pct ? 'Direct' : 'Total est.',
        details: [
          { label: 'Type', value: owner.type === 'company' ? 'Company' : 'Individual' },
          {
            label: 'Country',
            value: owner.country_iso || '—'
          }
        ]
      })),
      defaultExpandedHolderIds: owners[0]
        ? [owners[0].uci || 'holder-0']
        : [],
      ubo: ubo
        ? { label: 'Ultimate beneficial owner', value: ubo.name }
        : undefined,
      footerLabel: `Showing ${owners.length} of ${owners.length} shareholders`,
      footerLinkLabel: 'View in Orbis'
    },
    creditRisk: {
      sourceTag: 'Orbis',
      metrics: [
        {
          label: 'Falcon CRS',
          value: String(company?.falcon_risk_score ?? '—'),
          caption:
            company?.falcon_risk_score != null
              ? Number(company.falcon_risk_score) >= 4
                ? 'Passed threshold'
                : 'Below threshold'
              : undefined
        }
      ],
      signals: [
        {
          id: 'falcon',
          title: 'Falcon risk score',
          source: 'Orbis',
          value: String(company?.falcon_risk_score ?? '—'),
          caption:
            company?.falcon_risk_score != null
              ? `CRS ${company.falcon_risk_score}`
              : undefined
        },
        {
          id: 'sanctions',
          title: 'Sanctions',
          source: 'Orbis',
          value: flagged(company?.flags?.sanction) ? 'Match' : 'Clear',
          captionTone: flagged(company?.flags?.sanction) ? 'negative' : 'positive'
        },
        {
          id: 'pep',
          title: 'PEP',
          source: 'Orbis',
          value: flagged(company?.flags?.pep) ? 'Match' : 'Clear',
          captionTone: flagged(company?.flags?.pep) ? 'negative' : 'positive'
        },
        {
          id: 'adverse-media',
          title: 'Adverse media',
          source: 'Orbis',
          value: flagged(company?.flags?.adverse_media) ? 'Match' : 'Clear',
          captionTone: flagged(company?.flags?.adverse_media)
            ? 'negative'
            : 'positive'
        }
      ]
    }
  };
}
